import { useState } from "react";
import { motion } from "framer-motion";
import { ZoomIn } from "lucide-react";
import { FALLBACK, pick } from "@/lib/gallery";
import Lightbox from "@/components/landing/Lightbox";

const SPANS = [
  "md:col-span-2 md:row-span-2",
  "md:col-span-1",
  "md:col-span-1",
  "md:col-span-1",
  "md:col-span-1",
];

export default function Facilities({ gallery }) {
  const items = (gallery && gallery.length === 5 ? gallery : FALLBACK.fasilitas).map((it, i) => ({
    title: (it?.name && it.name.trim()) || FALLBACK.fasilitas[i]?.name || "",
    desc: (it?.desc && it.desc.trim()) || FALLBACK.fasilitas[i]?.desc || "",
    img: pick(it?.image, FALLBACK.fasilitas[i]?.image),
    span: SPANS[i],
  }));
  const lightboxItems = items.map((it) => ({ src: it.img, title: it.title, caption: it.desc }));
  const [openIndex, setOpenIndex] = useState(-1);
  return (
    <section
      id="fasilitas"
      className="relative bg-parchment py-24 md:py-32"
      data-testid="facilities-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-xs tracking-[0.4em] uppercase text-gold"
            >
              Fasilitas
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="mt-4 font-serif-display text-forest text-4xl md:text-5xl lg:text-6xl leading-[1.1]"
            >
              Lingkungan yang dibangun<br />
              <span className="italic text-gold">untuk menghidupkan hafalan.</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-moss max-w-md"
          >
            Lingkungan kondusif untuk menghafal Al-Qur&apos;an.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 md:auto-rows-[280px] gap-6">
          {items.map((f, i) => (
            <motion.button
              key={i}
              type="button"
              onClick={() => setOpenIndex(i)}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className={`group relative overflow-hidden rounded-2xl border border-gold/30 lift text-left w-full h-full cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-gold ${f.span}`}
              data-testid={`facility-${i}`}
              aria-label={f.title ? `Buka gambar ${f.title}` : `Buka gambar fasilitas ${i + 1}`}
            >
              <img
                src={f.img}
                alt={f.title}
                className="absolute inset-0 w-full h-full object-cover img-warm transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/90 via-forest/30 to-transparent" />
              <span className="absolute top-3 right-3 inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/85 text-forest opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <ZoomIn size={16} />
              </span>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                {f.title && <h3 className="font-serif-display text-parchment text-2xl md:text-3xl">{f.title}</h3>}
                {f.desc && <p className="mt-2 text-parchment/80 text-sm max-w-md">{f.desc}</p>}
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <Lightbox
        items={lightboxItems}
        index={openIndex}
        onClose={() => setOpenIndex(-1)}
        onChange={setOpenIndex}
        testIdPrefix="facilities-lightbox"
      />
    </section>
  );
}
